import { useEffect, useState, type ReactNode } from "react";
import { InformationNewsDetail } from "./InformationNewsDetail.tsx";
import { MobileSheet } from "./MobileSheet.tsx";
import { getInformationNewsExcerpt, type InformationNews } from "./informationFeed.ts";
import { translateEventText } from "../util/eventText.ts";
import i18n from "../util/i18n.ts";

const ROTATION_INTERVAL_MS = 5000;

const usePrefersReducedMotion = () => {
  const [reducedMotion, setReducedMotion] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(mediaQuery.matches);
    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  return reducedMotion;
};

const getHeadline = (news: InformationNews) =>
  getInformationNewsExcerpt(translateEventText(news), 96);

export const LeagueNewsTicker = ({ news }: { news: InformationNews[] }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [detailIndex, setDetailIndex] = useState<number | undefined>(undefined);
  const [paused, setPaused] = useState(false);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (activeIndex >= news.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, news.length]);

  useEffect(() => {
    if (news.length < 2 || paused || reducedMotion || detailIndex !== undefined) {
      return;
    }

    const timer = window.setInterval(() => {
      setActiveIndex((previous) => (previous + 1) % news.length);
    }, ROTATION_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [detailIndex, news.length, paused, reducedMotion]);

  if (news.length === 0) {
    return null;
  }

  const current = (news[activeIndex] ?? news[0])!;
  const detail = detailIndex === undefined ? undefined : news[detailIndex];
  const detailNavigation: ReactNode =
    detailIndex === undefined || news.length < 2 ? undefined : (
      <div className="d-flex gap-2 w-100">
        <button
          className="btn btn-outline-secondary flex-fill"
          disabled={detailIndex === 0}
          onClick={() => setDetailIndex((previous) => Math.max(0, (previous ?? 0) - 1))}
          type="button"
        >
          {i18n.t("leagueDashboard.newsPrevious", {
            ns: "ui",
            defaultValue: "Previous",
          })}
        </button>
        <button
          className="btn btn-outline-secondary flex-fill"
          disabled={detailIndex === news.length - 1}
          onClick={() =>
            setDetailIndex((previous) => Math.min(news.length - 1, (previous ?? 0) + 1))
          }
          type="button"
        >
          {i18n.t("leagueDashboard.newsNext", {
            ns: "ui",
            defaultValue: "Next",
          })}
        </button>
      </div>
    );

  return (
    <section className="mb-3" data-dashboard-league-news-ticker="">
      <h2 className="h6 mb-2">
        {i18n.t("leagueDashboard.newsTitle", {
          ns: "ui",
          defaultValue: "League news",
        })}
      </h2>
      <div
        className="league-news-ticker"
        data-paused={paused || reducedMotion ? "true" : "false"}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setPaused(false);
          }
        }}
        onFocus={() => setPaused(true)}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchEnd={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
      >
        <button
          aria-label={i18n.t("leagueDashboard.newsOpen", {
            ns: "ui",
            defaultValue: "Open league news: {{headline}}",
            headline: getHeadline(current),
          })}
          className="league-news-ticker-button"
          data-dashboard-league-news-headline={current.eid}
          onClick={() => setDetailIndex(activeIndex)}
          type="button"
        >
          <span className="league-news-ticker-headline" key={current.eid}>
            {getHeadline(current)}
          </span>
          <span aria-hidden="true" className="glyphicon glyphicon-chevron-right" />
        </button>
      </div>

      <MobileSheet
        footer={detailNavigation}
        onClose={() => setDetailIndex(undefined)}
        open={detail !== undefined}
        title={i18n.t("leagueDashboard.newsTitle", {
          ns: "ui",
          defaultValue: "League news",
        })}
      >
        {detail ? (
          <InformationNewsDetail
            news={detail}
            sourceLabel={i18n.t("leagueDashboard.newsSource", {
              ns: "ui",
              defaultValue: "League",
            })}
            title={i18n.t("information.newsFallbackTitle", {
              ns: "ui",
              defaultValue: "News update",
            })}
          />
        ) : null}
      </MobileSheet>
    </section>
  );
};

export default LeagueNewsTicker;
